import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  useTheme,
  alpha,
  Stack,
  Button,
  Switch,
  FormControlLabel,
  Badge,
  Tooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Search,
  FilterList,
  Person,
  Email,
  Phone,
  Circle,
  Refresh,
  Chat,
  Support,
  AccessTime,
  CheckCircle,
  Cancel,
  Wifi,
  WifiOff,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { socketService } from '../services/socket';
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLayout } from '../contexts/LayoutContext';

interface User {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  client_id: string;
  end_user_id: string;
  is_active: boolean;
  is_deleted: boolean;
  last_login: Date | null;
  created: Date;
  updated: Date;
  isOnline?: boolean;
  lastCheckIn?: Date;
}

const Users: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { preferences } = useLayout();
  const isSidebarLayout = preferences.layoutMode === 'sidebar';

  const clientId = searchParams.get('clientId');
  const endUserId = searchParams.get('endUserId');

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Fetch users from backend
  const fetchUsers = useCallback(() => {
    const socket = socketService.getSocket();
    if (!socket) {
      console.error('No socket connection');
      setLoading(false);
      return;
    }

    // If no clientId provided, fetch all users
    if (!clientId) {
      console.log('No clientId provided, fetching all users');
    }

    setLoading(true);
    socket.emit('getUsers', {
      clientId: clientId || undefined, // Optional clientId
      endUserId: endUserId || clientId, // Use clientId as fallback
      search: searchTerm,
      onlineOnly,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }, (response: any) => {
      if (response.success && response.data) {
        setUsers(response.data.users || []);
        setTotalCount(response.data.total || 0);
      } else {
        console.error('Failed to fetch users:', response.error);
        setUsers([]);
      }
      setLoading(false);
    });
  }, [clientId, endUserId, searchTerm, onlineOnly, page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
    
    // Set up real-time updates
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleUserStatusUpdate = (data: any) => {
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === data.userId 
            ? { ...user, isOnline: data.isOnline, lastCheckIn: data.lastCheckIn }
            : user
        )
      );
    };

    socket.on('userStatusUpdate', handleUserStatusUpdate);

    return () => {
      socket.off('userStatusUpdate', handleUserStatusUpdate);
    };
  }, [fetchUsers]);

  // Filter users locally
  useEffect(() => {
    let filtered = [...users];
    
    if (searchTerm) {
      filtered = filtered.filter(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const search = searchTerm.toLowerCase();
        return (
          fullName.includes(search) ||
          user.username.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search)
        );
      });
    }

    if (onlineOnly) {
      filtered = filtered.filter(user => user.isOnline);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, onlineOnly]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartChat = (userId: string) => {
    navigate(`/chat?userId=${userId}`);
  };

  const getStatusColor = (isOnline?: boolean) => {
    return isOnline ? theme.palette.success.main : theme.palette.action.disabled;
  };

  const getLastSeenText = (user: User) => {
    if (user.isOnline) return 'Online now';
    if (user.lastCheckIn) {
      return `Last seen ${formatDistanceToNow(new Date(user.lastCheckIn))} ago`;
    }
    if (user.last_login) {
      return `Last login ${formatDistanceToNow(new Date(user.last_login))} ago`;
    }
    return 'Never seen';
  };

  const UserCard = ({ user, index }: { user: User; index: number }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2, delay: index * 0.02 }}
    >
      <GlassCard gradient>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Circle
                    sx={{
                      fontSize: 14,
                      color: getStatusColor(user.isOnline),
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: '50%',
                    }}
                  />
                }
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    width: 48,
                    height: 48,
                  }}
                >
                  {user.first_name[0]}{user.last_name[0]}
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  @{user.username}
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={user.isOnline ? <Wifi sx={{ fontSize: 16 }} /> : <WifiOff sx={{ fontSize: 16 }} />}
              label={user.isOnline ? 'Online' : 'Offline'}
              size="small"
              color={user.isOnline ? 'success' : 'default'}
              variant={user.isOnline ? 'filled' : 'outlined'}
            />
          </Box>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary">
                {getLastSeenText(user)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user.is_active ? (
                <>
                  <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                  <Typography variant="body2" color="success.main">
                    Active account
                  </Typography>
                </>
              ) : (
                <>
                  <Cancel sx={{ fontSize: 16, color: theme.palette.error.main }} />
                  <Typography variant="body2" color="error.main">
                    Inactive account
                  </Typography>
                </>
              )}
            </Box>
          </Stack>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              size="small"
              variant="contained"
              startIcon={<Chat />}
              onClick={() => handleStartChat(user.id)}
              disabled={!user.isOnline}
            >
              Start Chat
            </Button>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<Support />}
              onClick={() => navigate(`/tickets?userId=${user.id}`)}
            >
              Tickets
            </Button>
          </Box>
        </Box>
      </GlassCard>
    </motion.div>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        mt: -3,
        pt: 3,
        ...(isSidebarLayout && {
          WebkitAppRegion: 'drag',
          '-webkit-app-region': 'drag',
        })
      }}>
        <Typography 
          variant="h4" 
          fontWeight={700} 
          gutterBottom
          sx={{
            display: 'inline-block',
            userSelect: 'text',
            WebkitAppRegion: 'no-drag',
            '-webkit-app-region': 'no-drag',
          }}
        >
          Users
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{
              display: 'inline-block',
              userSelect: 'text',
              WebkitAppRegion: 'no-drag',
              '-webkit-app-region': 'no-drag',
            }}
          >
            {totalCount > 0 ? `${totalCount} total users` : 'Manage user accounts'}
          </Typography>
          {users.filter(u => u.isOnline).length > 0 && (
            <Chip
              icon={<Circle sx={{ fontSize: 12 }} />}
              label={`${users.filter(u => u.isOnline).length} online`}
              size="small"
              color="success"
            />
          )}
        </Box>
      </Box>

      {/* Search and Filters */}
      <GlassCard sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search users..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={onlineOnly}
                  onChange={(e) => setOnlineOnly(e.target.checked)}
                  color="primary"
                />
              }
              label="Online only"
            />
            <Button
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('grid')}
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'table' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('table')}
            >
              Table
            </Button>
            <IconButton onClick={fetchUsers} size="small">
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      </GlassCard>

      {/* Users Display */}
      {loading ? (
        viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Skeleton variant="rounded" height={220} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Skeleton variant="rounded" height={400} />
        )
      ) : filteredUsers.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Person sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No users found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Try adjusting your search terms' : 
             onlineOnly ? 'No users are currently online' : 
             'Users will appear here once added'}
          </Typography>
        </Paper>
      ) : viewMode === 'grid' ? (
        <>
          <Grid container spacing={2}>
            <AnimatePresence mode="popLayout">
              {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user, index) => (
                <Grid key={user.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <UserCard user={user} index={index} />
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[12, 24, 48, 96]}
            sx={{ mt: 3 }}
          />
        </>
      ) : (
        <GlassCard>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Tooltip title={user.isOnline ? 'Online' : 'Offline'}>
                        <Circle sx={{ fontSize: 12, color: getStatusColor(user.isOnline) }} />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                          {user.first_name[0]}{user.last_name[0]}
                        </Avatar>
                        {user.first_name} {user.last_name}
                      </Box>
                    </TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getLastSeenText(user)}</TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip label="Inactive" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleStartChat(user.id)}
                          disabled={!user.isOnline}
                        >
                          <Chat />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/tickets?userId=${user.id}`)}
                        >
                          <Support />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </GlassCard>
      )}
    </Container>
  );
};

export default Users;